import { RefObject, useEffect, useRef } from "react";

/**
 * Runs `onOutsideClick` when a mousedown lands outside `ref`.
 *
 * `enabled` exists so a closed popup keeps no document listener: every dropdown mounted on the
 * page used to run its handler on every click anywhere in the app, and a handler that calls
 * something more than `setOpen(false)` (Select's `onOpenChange`) fired on all of them.
 *
 * The callback is kept in a ref, so callers do not have to wrap it in `useCallback` to avoid
 * re-subscribing on every render.
 */
export function useOutsideClick<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onOutsideClick: () => void,
  enabled = true,
): void {
  const callbackRef = useRef(onOutsideClick);

  useEffect(() => {
    callbackRef.current = onOutsideClick;
  }, [onOutsideClick]);

  useEffect(() => {
    if (!enabled) return;

    const handleMouseDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) callbackRef.current();
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [ref, enabled]);
}
